import { Entity, UpdateContext } from "./Entity";
import tank from "~../img/tank.png";
import { Renderer } from "./Renderer";
import { AssetLoader } from "./AssetLoader";
import { Background } from "./Background";
import { asyncLogLevel, L, log, logLevel } from "./log";
import { BulletType } from "./BulletType";
import { bulletTypes } from "~defs/BulletTypeDefs";
import { Engine } from "./Engine";
import { Bullet } from "./Bullet";
import { UpdateType } from "../../../backend/src/lib/types";

export enum InputDirection {
  NEGATIVE,
  POSITIVE,
  NONE,
}

export class Player extends Entity {
  direction: InputDirection;
  angleDirection: InputDirection;
  powerDirection: InputDirection;
  id: number;
  hp: number;
  angle: number;
  power: number;
  firing: boolean;
  weaponType: BulletType;
  cooldown: Record<string, number>;
  vx: number;
  connected: boolean;
  name: string;
  colour: string;
  respawnState: number;
  kills: number;
  deaths: number;

  static MAX_HP = 100;
  static MAX_POWER = 5;

  constructor(
    engine: Engine,
    x: number,
    id: number,
    name: string,
    colour: string,
    kills: number,
    deaths: number
  ) {
    super(engine, x, engine.background.heightMap[Math.round(x)]);
    this.direction = InputDirection.NONE;
    this.angleDirection = InputDirection.NONE;
    this.powerDirection = InputDirection.NONE;
    this.vx = 0;
    this.firing = false;
    this.cooldown = {};
    this.weaponType = bulletTypes["cannon"];
    this.angle = 0;
    this.power = 2.5;
    this.hp = Player.MAX_HP;
    this.id = id;
    this.connected = true;
    this.colour = colour;
    this.name = name;
    this.respawnState = 0;
    this.kills = kills;
    this.deaths = deaths;
  }

  update = asyncLogLevel(L.INFO, async (ctx: UpdateContext): Promise<void> => {
    if (!this.connected) return;
    const { bg } = ctx;

    // Input direction
    switch (this.direction) {
      case InputDirection.NEGATIVE:
        this.vx -= 0.03;
        break;
      case InputDirection.POSITIVE:
        this.vx += 0.03;
        break;
      case InputDirection.NONE:
        if (this.vx > 0) {
          this.vx = Math.max(0, this.vx - 0.05);
        } else if (this.vx < 0) {
          this.vx = Math.min(0, this.vx + 0.05);
        }
    }

    // Angle direction
    switch (this.angleDirection) {
      case InputDirection.NEGATIVE:
        this.angle -= 0.01;
        break;
      case InputDirection.POSITIVE:
        this.angle += 0.01;
        break;
    }
    while (this.angle < 0) this.angle += 2 * Math.PI;
    while (this.angle > 2 * Math.PI) this.angle -= 2 * Math.PI;

    // Velocity
    this.vx = Math.max(Math.min(this.vx, 1), -1);
    this.x += this.vx;

    // Wrapping
    if (this.x < (await AssetLoader.img(tank)).width) {
      this.x = Background.WIDTH - (await AssetLoader.img(tank)).width;
    }
    if (this.x > Background.WIDTH - (await AssetLoader.img(tank)).width) {
      this.x = (await AssetLoader.img(tank)).width;
    }
    this.y = bg.heightMap[Math.round(this.x)];

    // Power direction
    switch (this.powerDirection) {
      case InputDirection.NEGATIVE:
        this.power -= 0.1;
        break;
      case InputDirection.POSITIVE:
        this.power += 0.1;
        break;
    }
    this.power = Math.max(Math.min(this.power, Player.MAX_POWER), 0);

    // Reduce cooldowns
    this.cooldown[this.weaponType.id] = Math.max(
      this.cooldown[this.weaponType.id] - 1,
      0
    );

    // Firing
    if (this.firing && !this.cooldown[this.weaponType.id]) {
      log(L.DEBUG, `Firing ${this.weaponType.id}`);
      const power =
        this.power +
        Math.random() * this.weaponType.powerVariance * 2 -
        this.weaponType.powerVariance;
      const angle =
        this.angle +
        Math.random() * this.weaponType.angleVariance * 2 -
        this.weaponType.angleVariance;
      this.engine.bullets.spawnBullet({
        x: this.x,
        y: this.y - (await AssetLoader.img(tank)).height,
        vx: Math.cos(angle) * power * this.weaponType.powerMultiplier,
        vy: Math.sin(angle) * power * this.weaponType.powerMultiplier,
        type: this.weaponType,
        owner: this,
      });
      this.cooldown[this.weaponType.id] = this.weaponType.cooldown;
    }

    // Misc
    if (this.respawnState > 0) this.respawnState--;
  });

  damage(amount: number, causeType: string, causePid: number): void {
    this.hp = Math.max(this.hp - amount, 0);
    this.engine.remote.sendUpdate({
      type: UpdateType.PLAYER_HIT,
      pid: this.id,
      hp: this.hp,
    });
    if (this.hp === 0) {
      const respawnX = Math.random() * Background.WIDTH;
      this.engine.remote.sendUpdate({
        type: UpdateType.PLAYER_RESPAWN,
        pid: this.id,
        x: respawnX,
        causeType: causeType,
        causePid: causePid,
      });
      this.hp = Player.MAX_HP;
      this.x = respawnX;
      this.y = this.engine.background.heightMap[respawnX];
      this.vx = 0;
      this.respawnState = 25;
      this.deaths++;
      this.engine.players[causePid].kills++;
      this.engine.setScoreboard(
        this.engine.players.map((x) => ({
          name: x.name,
          colour: x.colour,
          kills: x.kills,
          deaths: x.deaths,
        }))
      );
      this.engine.pusher(
        `${this.engine.players[causePid].name} ${
          bulletTypes[causeType].name
        }'d ${this.id === causePid ? "themself" : this.name}`
      );
    }
  }

  async render(r: Renderer): Promise<void> {
    if (!this.connected) return;

    // Respawn halo
    if (this.respawnState > 0) {
      r.ctx.fillStyle = this.colour;
      r.ctx.beginPath();
      r.ctx.arc(this.x, this.y, this.respawnState, 0, 2 * Math.PI);
      r.ctx.closePath();
      r.ctx.fill();
    }

    // Actual tank
    const img = await AssetLoader.img(tank);
    r.ctx.fillStyle = this.colour;
    r.ctx.fillRect(
      this.x - img.width / 4,
      this.y - (5 * img.height) / 4,
      img.width / 2,
      img.height / 2
    );
    r.ctx.drawImage(img, this.x - img.width / 2, this.y - img.height);

    // Barrel
    r.ctx.strokeStyle = "#FFFFFF";
    r.ctx.beginPath();
    r.ctx.moveTo(this.x, this.y - img.height);
    r.ctx.lineTo(
      Math.floor(this.x + Math.cos(this.angle) * 6),
      Math.floor(this.y - img.height + Math.sin(this.angle) * 6)
    );
    r.ctx.closePath();
    r.ctx.stroke();

    // Healthbar
    r.ctx.fillStyle = "#000000";
    r.ctx.strokeStyle = "#FFFFFF";
    r.ctx.beginPath();
    r.ctx.rect(this.x - img.width / 2, this.y + 3, img.width, 5);
    r.ctx.closePath();
    r.ctx.stroke();
    r.ctx.fillRect(this.x - img.width / 2, this.y + 4, img.width, 3);
    r.ctx.fillStyle = "#FF0000";
    r.ctx.fillRect(
      this.x - img.width / 2,
      this.y + 4,
      (img.width * this.hp) / Player.MAX_HP,
      3
    );
  }

  /*
  async setDirection(direction: InputDirection, tickDelta: number = 0) {
    // This basically means 'apply to current state'
    if (tickDelta == 0) {
      this.direction = direction;
      return;
    }

    // Check that we actually remember that number of ticks in the past
    if (tickDelta >= this.history.length) {
      console.error(
        `Update received from ${tickDelta} ticks ago, but only have ${this.history.length} ticks in history. Ignoring update, but this will definitely cause a desync!`
      );
      return;
    }

    const initialState = this.history[tickDelta];
    this.x = initialState.x;
    this.y = initialState.y;
    this.vx = initialState.vx;
    this.history = initialState.history;
    this.direction = direction;
    for (let i = 0; i < tickDelta; i++) {}
  }
  */

  snapshot() {
    return {
      x: this.x,
      y: this.y,
      direction: this.direction,
      vx: this.vx,
      id: this.id,
    };
  }

  restore(snapshot: ReturnType<Player["snapshot"]>) {
    this.x = snapshot.x;
    this.y = snapshot.y;
    this.direction = snapshot.direction;
    this.vx = snapshot.vx;
    this.id = snapshot.id;
  }

  // static fromSnapshot(snapshot: ReturnType<Player["snapshot"]>): Player {
  //   const newPlayer = new Player(snapshot.x, snapshot.y, snapshot.id);
  //   newPlayer.restore(snapshot);
  //   return newPlayer;
  // }
}
